import React, { useEffect, useState } from "react"
import { Mutation } from "react-apollo"
import { ErrorMessage } from "@hookform/error-message"
import { useForm } from "react-hook-form"
import gql from "graphql-tag"
import Helmet from "react-helmet"
import qs from "qs"
import { logout } from "../../shared/auth"
import Confirm from "../../components/Confirm"
import "./resetPassword.scss"

const resetPasswordMutation = gql`
  mutation resetPassword($token: String, $newPassword: String!) {
    resetPassword(token: $token, newPassword: $newPassword) {
      id
      response {
        payload
        message
      }
    }
  }
`

const ResetPassword = (props) => {
  const { register, errors, handleSubmit, setError } = useForm()
  const [token, setToken] = useState()
  const [submitting, setSubmitting] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    const query = qs.parse(props.location.search, { ignoreQueryPrefix: true })
    if (!query.token) {
      setManualError(
        "submit",
        "This reset password link is invalid or expired."
      )
      return
    }
    setToken(query.token)

    if (props.viewer.isUserAuthenticated) {
      setShowConfirmation(true)
    }
  }, [])

  const setManualError = (name, msg) => {
    setError(name, { type: "manual", message: msg })
  }

  const onSubmit = (data) => {
    const { password, confirmPassword } = data

    if (password !== confirmPassword) {
      setManualError(
        "confirmPassword",
        "Those passwords didn't match. Try again."
      )
      return
    }

    if (token) {
      setSubmitting(true)
      props
        .resetPassword({
          variables: {
            token: token,
            newPassword: password,
          },
        })
        .then((res) => {
          const { payload } = res.data.resetPassword.response
          if (payload === "invalidResetPasswordToken") {
            setManualError(
              "submit",
              "This reset password link is invalid or expired."
            )
            return
          }

          if (payload === "alreadyLoggedIn") {
            setManualError(
              "submit",
              "You're already logged in. Please refresh the page and try again."
            )
            return
          }

          if (payload === "resetPasswordError") {
            throw new Error("Unknown error occurred.")
          }

          window.location.replace("/")
        })
        .catch(() => {
          setManualError(
            "submit",
            "An unknown error occurred. Please refresh the page and try again."
          )
        })
        .finally(() => setSubmitting(false))
    }
  }

  const handleConfirmClick = () => {
    logout().then(() => window.location.reload())
  }

  const handleCancelClick = () => {
    window.location.replace("/")
  }

  const minLengthRule = {
    value: 8,
    message: "Password must be at least 8 characters",
  }

  return (
    <div id="reset-password">
      <Confirm
        title="You're logged in!"
        message="Logout to reset your password."
        isOpen={showConfirmation}
        confirm={{ text: "logout", action: handleConfirmClick }}
        cancel={{ text: "cancel", action: handleCancelClick }}
      />
      <Helmet>
        <title>Reset Password</title>
      </Helmet>
      <form
        className="reset-password-form"
        style={{ textAlign: "start" }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <h2>Reset Password</h2>
        <section className="form-group">
          <ErrorMessage
            name="password"
            errors={errors}
            as={<span className="field-error" />}
          />
          <input
            data-cy="new-password"
            disabled={!token}
            name="password"
            className="form-control"
            type="password"
            placeholder="New password"
            ref={register({
              required: "New password is required",
              minLength: minLengthRule,
            })}
          />
        </section>
        <section className="form-group">
          <ErrorMessage
            name="confirmPassword"
            errors={errors}
            as={<span className="field-error" />}
          />
          <input
            data-cy="confirm-password"
            disabled={!token}
            name="confirmPassword"
            className="form-control"
            type="password"
            placeholder="Re-enter new password"
            ref={register({
              required: "Password confirmation is required",
              minLength: minLengthRule,
            })}
          />
        </section>
        {errors.submit && (
          <div className="password-reset-error" style={{ textAlign: "center" }}>
            <div>
              <p>{errors.submit.message}</p>
              <a data-cy="request-new" href="/login?forgotPassword=true">
                Request a new link
              </a>
            </div>
          </div>
        )}
        <button
          disabled={!token || submitting}
          data-cy="reset-submit"
          className="btn btn-primary"
          type="submit"
          style={{ width: "100%" }}
        >
          reset password
        </button>
      </form>
    </div>
  )
}

const ResetPasswordWithMutation = (props) => {
  return (
    <Mutation mutation={resetPasswordMutation}>
      {(mutate) => <ResetPassword resetPassword={mutate} {...props} />}
    </Mutation>
  )
}

export default ResetPasswordWithMutation
